import React, { useState, useEffect } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import { FaSpinner, FaTimes } from 'react-icons/fa';
import api from '../../api';

import MeetingOnlyOfficeViewerModal from './MeetingOnlyOfficeViewerModal';
import '../FileManager/ImageViewerModal.css';
import '../FileManager/PdfViewerModal.css';
import '../FileManager/OnlyOfficeViewerModal.css';

const IMAGE_EXT  = ['jpg','jpeg','png','gif','svg','webp','bmp','tiff','tif','ico','avif'];
const PDF_EXT    = ['pdf'];
const OFFICE_EXT = ['doc','docx','odt','rtf','xls','xlsx','ods','csv','ppt','pptx','odp'];

const getExt     = (name) => (name || '').split('.').pop().toLowerCase();
const isImage    = (name) => IMAGE_EXT.includes(getExt(name));
const isPdf      = (name) => PDF_EXT.includes(getExt(name));
const isOffice   = (name) => OFFICE_EXT.includes(getExt(name));

const fetchBlob = async (meetingId, attachmentId, mimeType = 'application/octet-stream') => {
    const response = await api.get(
        `/meetings/${meetingId}/attachments/${attachmentId}/download`,
        { responseType: 'blob' }
    );
    const blob = new Blob([response], { type: mimeType });
    return window.URL.createObjectURL(blob);
};

/* ─────────────────────────────────────────────
   Image viewer
   - Uses attachment.url directly as Lightbox src (public storage URL)
   - No blob fetching — zero extra API calls for images
   - Opens on the correct clicked slide immediately
───────────────────────────────────────────── */
const MeetingImageViewer = ({ attachment, allAttachments, onClose }) => {
    const attachmentType = attachment.type ?? 'attachment';
    const imageAttachments = (allAttachments || [attachment]).filter(
        a => isImage(a.file_name) && (a.type ?? 'attachment') === attachmentType
    );

    const initialIndex = Math.max(0, imageAttachments.findIndex(a => a.id === attachment.id));
    const [currentIndex, setCurrentIndex] = useState(initialIndex);

    const slides = imageAttachments.map(a => ({
        src: a.url,
        title: a.file_name,
    }));

    return (
        <Lightbox
            open={true}
            close={onClose}
            slides={slides}
            index={currentIndex}
            on={{ view: ({ index }) => setCurrentIndex(index) }}
            plugins={[Zoom]}
            animation={{ fade: 250, swipe: 250 }}
            carousel={{ finite: imageAttachments.length <= 1 }}
            controller={{ closeOnBackdropClick: true }}
            styles={{ container: { backgroundColor: 'rgba(0,0,0,0.95)' } }}
            zoom={{
                maxZoomPixelRatio: 3,
                zoomInMultiplier: 2,
                doubleClickMaxStops: 2,
                keyboardMoveDistance: 50,
                wheelZoomDistanceFactor: 100,
            }}
        />
    );
};

/* ─────────────────────────────────────────────
   PDF viewer — exact copy of FileSync's PdfViewerModal
───────────────────────────────────────────── */
const MeetingPdfViewer = ({ attachment, meetingId, onClose }) => {
    const [viewUrl, setViewUrl] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError]     = useState(null);

    useEffect(() => {
        if (!attachment?.id) { setError('No file provided'); setLoading(false); return; }
        let objectUrl = null;
        setLoading(true);
        setError(null);

        fetchBlob(meetingId, attachment.id, 'application/pdf')
            .then(url => { objectUrl = url; setViewUrl(url); })
            .catch(err => setError(err.message || 'Failed to load PDF'))
            .finally(() => setLoading(false));

        return () => { if (objectUrl) window.URL.revokeObjectURL(objectUrl); };
    }, [attachment, meetingId]);

    if (!attachment) return null;

    return (
        <div className="pdf-viewer-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="pdf-viewer-modal">
                <div className="pdf-viewer-header">
                    <h3 className="pdf-viewer-title">{attachment.file_name || 'PDF Document'}</h3>
                    <button className="pdf-viewer-close" onClick={onClose} aria-label="Close PDF viewer">
                        <FaTimes />
                    </button>
                </div>
                <div className="pdf-viewer-content">
                    {loading && (
                        <div className="pdf-viewer-loading">
                            <FaSpinner className="pdf-viewer-spinner" />
                            <p>Loading PDF...</p>
                        </div>
                    )}
                    {error && !loading && (
                        <div className="pdf-viewer-error">
                            <p>{error}</p>
                            <button onClick={onClose}>Close</button>
                        </div>
                    )}
                    {!loading && !error && viewUrl && (
                        <iframe src={viewUrl} className="pdf-viewer-iframe" title={attachment.file_name || 'PDF Document'} />
                    )}
                </div>
            </div>
        </div>
    );
};


/* ─────────────────────────────────────────────
   Router
───────────────────────────────────────────── */
const MeetingAttachmentPreviewModal = ({ attachment, allAttachments, meetingId, onClose }) => {
    if (!attachment) return null;

    if (isImage(attachment.file_name))  return <MeetingImageViewer          attachment={attachment} allAttachments={allAttachments} meetingId={meetingId} onClose={onClose} />;
    if (isPdf(attachment.file_name))    return <MeetingPdfViewer             attachment={attachment} meetingId={meetingId} onClose={onClose} />;
    if (isOffice(attachment.file_name)) return <MeetingOnlyOfficeViewerModal attachment={attachment} meetingId={meetingId} onClose={onClose} />;

    return (
        <div className="pdf-viewer-overlay" onClick={onClose}>
            <div className="pdf-viewer-error" style={{ color: '#fff' }}>
                <p>Preview not available for this file type.</p>
                <button onClick={onClose}>Close</button>
            </div>
        </div>
    );
};

export default MeetingAttachmentPreviewModal;
